import { apiClient } from './client'
import type {
  TcmEnablementStatus,
  TcmEnablementVerifyPayload,
  TcmEnablementVerifyRequest,
  TcmEnablementVerifyResult,
  TcmShadowEnableRequest,
  TcmPurviewComplianceEnableRequest,
  TcmMonitorVersion,
  TcmMonitorRefreshRequest,
  TcmSnapshotJob,
  TcmSnapshotCreateRequest,
  TcmDriftSummary,
  TcmDriftDetail,
  TcmDriftListResponse,
  TcmDriftStateUpdateRequest,
  TcmIngestionStateRow,
  TcmIngestionTriggerResponse,
  TcmResourceCatalogEntry,
  TcmResourceCatalogResponse,
  TcmMonitoringProfilesResponse,
  TcmMonitoringBudgetSnapshot,
  TcmSnapshotQuotaSnapshot,
  TcmBaselineWriteResult,
  TcmStageResult,
  TcmUnstageResult,
  TcmDriftAuditHistoryResponse,
  TcmDriftApprovalImpact,
  DriftReconciliationReport,
  TcmCoverageWaiver,
  TcmCoverageSummary,
  TcmLicenseEligibility,
  TcmCapacityScan,
} from '@/types/tcm'

// > the Function proxy's 2 min default (ContainerProxy.DefaultTimeout), < the 230 s
// Azure front-end cap. See applyStagedTcmDrifts.
export const APPLY_STAGED_TIMEOUT_MS = 150_000

/**
 * TCM Lab API (Wave 3 W3-G). All endpoints Platform-Admin-gated during the
 * side-by-side period. Routes proxy through TcmFunction (W3-D) to
 * Tcm{Enablement,Admin,Drift}Controller in the Container. Split out of
 * `lib/api.ts` (WO-10.4) — relocated verbatim, including the pre-existing
 * `tcmApi` self-reference in `listDriftsItems` below (documented in place;
 * not something this split changes).
 */

// #18 S2 — `source` discriminator carried by every TCM drift row. Mirrors the
// C# `TcmDriftEntity.Source`. Defined here (not in @/types/tcm — that file is
// outside the T5 write-scope this wave) and intersected onto the drift read
// shapes below so callers can branch on a detector row without an `any`.
// Optional so a row from an older build (or a not-yet-widened mock) still
// type-checks; callers treat the absence as 'Monitor'. The canonical interfaces
// in @/types/tcm should adopt this field directly in a follow-up.
//
// Members mirror the backend Source values that reach the read shapes:
//   - 'Monitor'           — an ordinary change to a watched setting (default).
//   - 'CreationDetector'  — a brand-new policy spotted between checks (#18 S2,
//                           `driftType==='Added'`); accept folds it IN.
//   - 'DeletionDetector'  — a watched policy that vanished from the tenant
//                           (deletion detector, `driftType==='Removed'`);
//                           accept retires it OUT of the watched baseline.
//   - 'SnapshotDiff'      — a recurring config-sweep finding (#14 Tier-2); its
//                           `Removed` rows share the same retire-OUT accept leg.
// Only `DeletionDetector`/`SnapshotDiff` + `Removed` rows promise a retire — the
// copy layer gates on the source so a `Monitor` `Removed` (not retire-eligible
// in the backend's `IsRetireRow`) never shows a retire promise it can't keep.
export type TcmDriftPipelineSource =
  | 'Monitor'
  | 'CreationDetector'
  | 'DeletionDetector'
  | 'SnapshotDiff'

// ── Drift Triage Copilot (WO-3 phase 1.5) wire shapes ────────────────
// Tally returned by the PA-only triage-run endpoint. `bySuggestion` is a
// sparse map keyed by the `TriageSuggestion` values actually produced (Phase
// 1 only ever emits `ExpectedChurn`). Counts are honest — `skippedFloor` is
// the number of security-sensitive rows forced to RealChange, `failedCohorts`
// the AI cohorts that errored and were left un-stamped for a later retry.
export interface DriftTriageRunTally {
  tenantId: string
  tcmRowsScanned: number
  dscRowsScanned: number
  bySuggestion: Record<string, number>
  skippedFloor: number
  cohorts: number
  aiCalls: number
  failedCohorts: number
}

// Result of the TCM bulk-waive endpoint. Every count is server-side truth so
// the caller can name exactly what happened (and what was skipped) rather than
// assume full success. Staged rows are skipped (they're mid-apply), already-
// waived rows are idempotent no-ops, and not-found ids are reported, not
// swallowed.
export interface TcmBulkWaiveResult {
  tenantId: string
  waivedCount: number
  skippedStagedCount: number
  alreadyWaivedCount: number
  /** Terminal rows (already accepted, resolved, or rejected) the server refused to regress. */
  skippedClosedCount: number
  notFoundCount: number
}

export type TcmDriftSummaryWithSource = TcmDriftSummary & { source?: TcmDriftPipelineSource }
export type TcmDriftDetailWithSource = TcmDriftDetail & { source?: TcmDriftPipelineSource }
export interface TcmDriftListResponseWithSource extends Omit<TcmDriftListResponse, 'items'> {
  items: TcmDriftSummaryWithSource[]
}
// Wire DTOs for the customer self-service journey endpoints live in the pure
// action lib (types/tcm.ts is owned by another slice; type-only import — no
// runtime cycle with this module).
import type {
  TcmScopeResult,
  TcmSizingStartResult,
  TcmSizingStatus,
} from '../monitoring-actions'

export const tcmApi = {
  // ── Enablement ────────────────────────────────────────────────────
  async getEnablementStatus(tenantId: string): Promise<TcmEnablementStatus> {
    const response = await apiClient.get(`/tcm/enablement/${tenantId}/status`)
    return response.data
  },

  // Returns the rendered PowerShell script as text/plain. Caller can
  // download via a Blob URL or paste into a code-block UI.
  async generateEnablementScript(tenantId: string): Promise<string> {
    const response = await apiClient.post(
      `/tcm/enablement/${tenantId}/generate-script`,
      null,
      { responseType: 'text', transformResponse: [(d) => d] }
    )
    return response.data as string
  },

  async verifyEnablement(
    tenantId: string,
    body: TcmEnablementVerifyRequest
  ): Promise<TcmEnablementVerifyResult> {
    const response = await apiClient.post(
      `/tcm/enablement/${tenantId}/verify`,
      body
    )
    return response.data
  },

  async toggleShadowEnable(
    tenantId: string,
    body: TcmShadowEnableRequest
  ): Promise<void> {
    await apiClient.post(`/tcm/enablement/${tenantId}/shadow-enable`, body)
  },

  async togglePurviewComplianceEnable(
    tenantId: string,
    body: TcmPurviewComplianceEnableRequest
  ): Promise<void> {
    await apiClient.post(`/tcm/enablement/${tenantId}/purview-compliance-enable`, body)
  },

  // ── Monitors ──────────────────────────────────────────────────────
  async listMonitors(tenantId: string): Promise<TcmMonitorVersion[]> {
    const response = await apiClient.get(`/tcm/admin/monitors/${tenantId}`)
    return response.data
  },

  async refreshMonitor(
    tenantId: string,
    body: TcmMonitorRefreshRequest
  ): Promise<TcmMonitorVersion> {
    const response = await apiClient.post(
      `/tcm/admin/monitors/${tenantId}/refresh`,
      body
    )
    return response.data
  },

  async deleteMonitor(tenantId: string, monitorId: string, confirm?: true): Promise<void> {
    // `confirm=true` is required by the Container when deleting the tenant's
    // active (live) monitor — the server 409s without it as a live-monitor guard.
    // Superseded distinct-id (legacy orphan) deletes omit the param.
    await apiClient.delete(`/tcm/admin/monitors/${tenantId}/${monitorId}`, {
      params: confirm ? { confirm: 'true' } : undefined,
    })
  },

  // ── Snapshots ─────────────────────────────────────────────────────
  async listSnapshots(
    tenantId: string,
    limit = 50
  ): Promise<TcmSnapshotJob[]> {
    const response = await apiClient.get(
      `/tcm/admin/snapshots/${tenantId}`,
      { params: { limit } }
    )
    return response.data
  },

  async createSnapshot(
    tenantId: string,
    body: TcmSnapshotCreateRequest
  ): Promise<TcmSnapshotJob> {
    const response = await apiClient.post(
      `/tcm/admin/snapshots/${tenantId}/create`,
      body
    )
    return response.data
  },

  async getSnapshotStatus(
    tenantId: string,
    jobId: string
  ): Promise<TcmSnapshotJob> {
    const response = await apiClient.get(
      `/tcm/admin/snapshots/${tenantId}/${jobId}/status`
    )
    return response.data
  },

  // ── Drifts ────────────────────────────────────────────────────────
  //
  // #18 S2 — every TCM drift row now carries `source`: which Guardian pipeline
  // produced it. 'Monitor' = an ordinary change to a watched setting (the
  // default the backend backfills onto historical rows); 'CreationDetector' =
  // a brand-new policy Guardian spotted between checks. The field is mirrored
  // from the C# `TcmDriftSummaryDto`/`TcmDriftDetailDto.Source` added in T1. We
  // intersect it onto the return shapes HERE (api.ts is T5's DTO seam per the
  // blueprint §7 file list); the canonical wire interfaces in `@/types/tcm`
  // and the `UnifiedDriftRow` normalisers + the drawer must be widened too to
  // carry it end-to-end — see the T5 deliverable's out-of-scope note.
  async listDrifts(
    tenantId: string,
    opts?: {
      page?: number
      pageSize?: number
      state?: string
      resourceType?: string
      // Wave 7 (R-NEW-5): 'severity' opts in to (Severity DESC,
      // FirstReportedDateTime DESC) — required by the Hybrid merge view.
      sortBy?: 'severity'
      // WO-3: include non-RealChange rows. No-op today (all TCM rows are
      // RealChange in phase 1) — wired for parity + forward-compat. Pass-through
      // query param; the TcmFunction proxy forwards the query string.
      includeFiltered?: boolean
      // Tier-naming follow-up (Adam, 2026-09-23): narrow both `items` AND the
      // server-side `totalCount` to one Source, or to everything except one
      // Source. Lets a caller get an honest per-tier total (Tier-1 continuous
      // watch vs. Tier-2 recurring-sweep) with a cheap pageSize:1 call instead
      // of counting a loaded page client-side. Mutually independent — pass at
      // most one in practice.
      source?: TcmDriftPipelineSource
      excludeSource?: TcmDriftPipelineSource
    }
  ): Promise<TcmDriftListResponseWithSource> {
    const response = await apiClient.get(`/tcm/drifts/${tenantId}`, {
      params: opts,
    })
    return response.data
  },

  async getDrift(tenantId: string, driftId: string): Promise<TcmDriftDetailWithSource> {
    const response = await apiClient.get(`/tcm/drifts/${tenantId}/${driftId}`)
    return response.data
  },

  async updateDriftState(
    tenantId: string,
    driftId: string,
    body: TcmDriftStateUpdateRequest
  ): Promise<void> {
    await apiClient.patch(
      `/tcm/drifts/${tenantId}/${driftId}/status`,
      body
    )
  },

  // ── Drift lifecycle (Wave 5 — approve / reject; Accept-via-Staging
  //    blueprint §5.1 — stage / unstage / apply) ────────────────────
  // All calls take `tenantId` explicitly. Tenant isolation is enforced
  // server-side; the client never short-circuits these routes through
  // `defaultTenantId`. The caller passes the tenant the table is showing.
  //
  // STAGING REWORK (Accept-via-Staging blueprint): approve / bulk-approve
  // no longer re-baseline. They flip the drift to State=AcceptedPending — an
  // instant, cheap, 409-free DB write — and return the tenant's total staged
  // count (TcmStageResult). The expensive batched baseline write happens once,
  // later, via `applyStagedTcmDrifts`. Same URLs as before; only the response
  // shape and server behaviour changed.
  async approveTcmDrift(
    tenantId: string,
    driftId: string,
    comment?: string,
  ): Promise<TcmStageResult> {
    const response = await apiClient.post(
      `/tcm/drifts/${tenantId}/${driftId}/approve`,
      { comment },
    )
    return response.data
  },

  // Reject is fire-and-forget from the client's POV — the controller stamps
  // RejectedBy/RejectedAt/RejectionComments and returns 204. Caller refetches
  // via tcm-drifts query invalidation.
  async rejectTcmDrift(
    tenantId: string,
    driftId: string,
    comment?: string,
  ): Promise<void> {
    await apiClient.post(
      `/tcm/drifts/${tenantId}/${driftId}/reject`,
      { comment },
    )
  },

  // Bulk-approve takes one comment for the whole batch — the audit log
  // gets N rows with the same Comments string. driftIds are server-validated
  // (each must resolve in the tenant). Stages all of them as AcceptedPending
  // (see staging note above) and returns the tenant's total staged count.
  async bulkApproveTcmDrifts(
    tenantId: string,
    driftIds: string[],
    comment?: string,
  ): Promise<TcmStageResult> {
    const response = await apiClient.post(
      `/tcm/drifts/${tenantId}/bulk-approve`,
      { driftIds, comment },
    )
    return response.data
  },

  // Apply ALL of a tenant's staged (AcceptedPending) drifts in one batch →
  // ONE new TcmMonitorVersion (the expensive re-baseline). All-or-nothing:
  // the Container 409s if nothing is staged or if the active monitor advanced
  // out-of-band since staging (the caller surfaces the server message — copy
  // says "re-scan and re-stage"). Returns the new monitor id/hash/drift count.
  // Applying a new policy waits up to 90 s for its TCM capture, so the 30 s client
  // default gave up while the server was still working and showed a false failure.
  // Wait out the Function proxy's 2 min timeout instead, so the toast always gets the
  // server's answer (applied, the "still capturing" 409, or the proxy's 502).
  async applyStagedTcmDrifts(
    tenantId: string,
    comment?: string,
  ): Promise<TcmBaselineWriteResult> {
    const response = await apiClient.post(
      `/tcm/drifts/${tenantId}/apply-staged`,
      { comment },
      { timeout: APPLY_STAGED_TIMEOUT_MS },
    )
    return response.data
  },

  // Discard a staging: flip the given AcceptedPending rows back to Active.
  // No-op server-side for ids not currently staged (already applied/rejected
  // elsewhere). Returns `revertedCount` (rows actually flipped back — NOT the
  // requested count) and the tenant's remaining staged total. The caller MUST
  // compare revertedCount to the requested count: a shortfall means the Undo
  // was a no-op for some rows and the toast must say so (finding F6).
  async unstageTcmDrifts(
    tenantId: string,
    driftIds: string[],
  ): Promise<TcmUnstageResult> {
    const response = await apiClient.post(
      `/tcm/drifts/${tenantId}/unstage`,
      { driftIds },
    )
    return response.data
  },

  async getTcmDriftAuditHistory(
    tenantId: string,
    driftId: string,
  ): Promise<TcmDriftAuditHistoryResponse> {
    const response = await apiClient.get(
      `/tcm/drifts/${tenantId}/${driftId}/audit-history`,
    )
    return response.data
  },

  async getTcmDriftApprovalImpact(
    tenantId: string,
    driftId: string,
  ): Promise<TcmDriftApprovalImpact> {
    const response = await apiClient.get(
      `/tcm/drifts/${tenantId}/${driftId}/approval-impact`,
    )
    return response.data
  },

  // ── Drift Triage Copilot (WO-3 phase 1.5) ─────────────────────────
  // Run one triage pass over the tenant's open, untriaged drift rows
  // (both sources). PA-only server-side. Returns a tally; writes only the
  // advisory Triage* columns — nothing leaves any queue until a human
  // approves. Explicit return-type generic for the same TS7022
  // self-reference reason as getMonitoringBudget below.
  async runTriage(tenantId: string): Promise<DriftTriageRunTally> {
    const response = await apiClient.post<DriftTriageRunTally>(
      `/tcm/admin/triage/${tenantId}`,
    )
    return response.data
  },

  // Bulk-waive TCM rows (State → 'Waived'). Tenant-access gated (not
  // PA-only) — the caller passes the tenant the queue is showing. Mirrors
  // the bulk-approve shape (driftIds + one shared comment). Returns
  // per-outcome counts so a partial run (staged / already-waived / missing
  // rows) is surfaced, never assumed away. Explicit return-type generic for
  // the same TS7022 self-reference reason as getMonitoringBudget below.
  async bulkWaiveTcmDrifts(
    tenantId: string,
    driftIds: string[],
    comment?: string,
  ): Promise<TcmBulkWaiveResult> {
    const response = await apiClient.post<TcmBulkWaiveResult>(
      `/tcm/drifts/${tenantId}/bulk-waive`,
      { driftIds, comment },
    )
    return response.data
  },

  // ── Ingestion ─────────────────────────────────────────────────────
  async getIngestionState(tenantId: string): Promise<TcmIngestionStateRow[]> {
    const response = await apiClient.get(
      `/tcm/admin/ingestion-state/${tenantId}`
    )
    return response.data
  },

  async triggerIngestion(
    tenantId: string
  ): Promise<TcmIngestionTriggerResponse> {
    const response = await apiClient.post(
      `/tcm/admin/ingestion/${tenantId}/trigger`
    )
    return response.data
  },

  // ── Promote-to-Hybrid (Wave 7) ────────────────────────────────────
  // Platform-Admin-only. Server-side pre-flight:
  //   - TcmEnablementStatus.Status === 'Enabled'
  //   - >=2 completed ingestion cycles in TcmIngestionState
  //   - >=1 active TcmMonitorVersionEntity row
  // 409 = pre-flight failed (server returns a message naming the
  // unmet condition — surface verbatim); 200 = baselineSource flipped
  // to 'Hybrid' and the four tenant-shaped cache keys were
  // invalidated server-side.
  async promoteToHybrid(
    tenantId: string,
  ): Promise<{ tenantId: string; baselineSource: string }> {
    const response = await apiClient.post(
      `/tcm/admin/tenants/${tenantId}/promote-to-hybrid`,
    )
    return response.data
  },

  // ── Drift Reconciliation report (Wave 8) ──────────────────────────
  // Platform-Admin-only read-only diagnostic. Compares active DSC vs
  // active TCM drifts for one tenant and returns a per-pair match
  // breakdown + per-resource-type rollup. Used to decide if a tenant
  // is ready to flip BaselineSource from 'DSC' to 'Hybrid'.
  //
  // 200 = report computed (may still be empty if both sources are
  // empty); 404 = tenant not found; 403 = caller is not PA (shouldn't
  // happen — `/tcm-lab` already gates this).
  async getReconciliationReport(
    tenantId: string,
  ): Promise<DriftReconciliationReport> {
    const response = await apiClient.get<DriftReconciliationReport>(
      `/tcm/admin/reconciliation-report/${tenantId}`,
    )
    return response.data
  },

  // ── Coverage waivers (P1 — coverage-breadth promote gate) ─────────
  // PA-only. A waiver is the explicit "known limitation" N/A channel
  // for a resource type the breadth gate would otherwise block on.
  // 409 from create = an active waiver already exists for the type.
  async listCoverageWaivers(tenantId: string): Promise<TcmCoverageWaiver[]> {
    const response = await apiClient.get<TcmCoverageWaiver[]>(
      `/tcm/admin/tenants/${tenantId}/coverage-waivers`,
    )
    return response.data
  },

  async createCoverageWaiver(
    tenantId: string,
    resourceType: string,
    reason: string,
  ): Promise<TcmCoverageWaiver> {
    const response = await apiClient.post<TcmCoverageWaiver>(
      `/tcm/admin/tenants/${tenantId}/coverage-waivers`,
      { resourceType, reason },
    )
    return response.data
  },

  async revokeCoverageWaiver(
    tenantId: string,
    waiverId: string,
  ): Promise<TcmCoverageWaiver> {
    const response = await apiClient.post<TcmCoverageWaiver>(
      `/tcm/admin/tenants/${tenantId}/coverage-waivers/${waiverId}/revoke`,
    )
    return response.data
  },

  // Convenience: drift summary list is sometimes nested under .items, sometimes
  // returned bare. Use this when you specifically want the raw array.
  async listDriftsItems(tenantId: string, opts?: Parameters<typeof tcmApi.listDrifts>[1]): Promise<TcmDriftSummary[]> {
    const page = await tcmApi.listDrifts(tenantId, opts)
    return page.items
  },

  // ── Resource catalogue (Wave 5A) ──────────────────────────────────
  // Returns the static catalogue of TCM resource types the picker UI
  // should expose. Server-side cached for the process lifetime; callers
  // should treat it as static-per-session (staleTime: Infinity).
  async listResourceTypes(): Promise<TcmResourceCatalogEntry[]> {
    const response = await apiClient.get<TcmResourceCatalogResponse>(
      '/tcm/admin/resource-types'
    )
    return response.data.entries
  },

  // ── Monitoring profiles (monitoring-scope plan Phase E) ───────────
  // Returns the named monitoring-profile bundles + the per-tenant budget.
  // Like the catalogue, this is static-per-session (server-derived from the
  // catalogue's monitoringGroup tagging), so callers should treat it as
  // static (staleTime: Infinity). Unlike listResourceTypes(), we return the
  // whole envelope — the caller needs budget + monitoringGroups, not just
  // the profiles array.
  async listMonitoringProfiles(): Promise<TcmMonitoringProfilesResponse> {
    const response = await apiClient.get<TcmMonitoringProfilesResponse>(
      '/tcm/admin/monitoring-profiles'
    )
    return response.data
  },

  // ── Per-tenant monitoring budget (tcm-pertenant-budget plan Phase C) ──
  // Returns the REAL per-group resource-INSTANCE counts for a tenant, sized
  // from its latest broad/discovery snapshot, so the picker can show
  // "N / 200 instances (as of <date>)". Tenant-specific (unlike the static
  // profiles), and changes only when a new snapshot lands — callers use a
  // short staleTime (~5min), not Infinity. Explicit return-type generic: tcmApi
  // is inferred `any` repo-wide (a pre-existing TS7022 self-reference on this
  // object literal), so without it the response degrades to `any`.
  async getMonitoringBudget(tenantId: string): Promise<TcmMonitoringBudgetSnapshot> {
    const response = await apiClient.get<TcmMonitoringBudgetSnapshot>(
      `/tcm/admin/monitoring-budget/${tenantId}`
    )
    return response.data
  },

  // ── Per-tenant snapshot-quota meter (snapshot-diff Phase D, §D.1) ────
  // DISTINCT from getMonitoringBudget above: that returns the advisory
  // 200-INSTANCE monitor budget; THIS returns the 20,000-RESOURCES/month
  // SNAPSHOT quota the recurring config-only sweep burns, plus last-sweep
  // provenance + the per-tenant sweep cadence. PA-only (the lab is PA-only).
  // Changes only when a sweep archives — callers use a short staleTime
  // (~5min). Explicit return-type generic for the same TS7022 reason as
  // getMonitoringBudget above (without it the response degrades to `any`).
  async getSnapshotQuota(tenantId: string): Promise<TcmSnapshotQuotaSnapshot> {
    const response = await apiClient.get<TcmSnapshotQuotaSnapshot>(
      `/tcm/admin/snapshot-quota/${tenantId}`
    )
    return response.data
  },

  // ── Customer-facing monitoring coverage (Monitoring tab) ─────────────
  // Tenant-scoped (NOT platform-admin) read powering the Baseline
  // Compliance Monitoring tab: what Guardian watches for this tenant, the
  // sizing provenance dates, and where the tenant sits in the setup
  // journey. Changes when a watch list or capture lands — short staleTime
  // (~5min). Explicit return-type generic for the same TS7022 reason as
  // getMonitoringBudget above.
  async getCoverage(tenantId: string): Promise<TcmCoverageSummary> {
    const response = await apiClient.get<TcmCoverageSummary>(
      `/tcm/coverage/${tenantId}`
    )
    return response.data
  },

  // ── Customer self-service setup journey (Phase 2 blueprint §6) ───────
  // Six tenant-scoped journey actions on /tcm/coverage/{tenantId}/… —
  // distinct from the PA-only /tcm/enablement|admin routes above. Gated
  // server-side by (Platform Admin) OR (caller has tenant access); the
  // client just passes the tenant the Monitoring tab is showing.

  // License pre-flight (tcm-license-eligibility, 2026-07-29): does this
  // tenant's Microsoft subscription clear Microsoft's floor for the
  // configuration-management service? Queried by the setup journey BEFORE
  // offering the script download; 'unknown' = check failed server-side, the
  // caller shows nothing (fail-open). Static per session in practice —
  // licensing changes out-of-band — so callers use a long staleTime. Explicit
  // return-type generic for the same TS7022 reason as getMonitoringBudget.
  async getLicenseEligibility(tenantId: string): Promise<TcmLicenseEligibility> {
    const response = await apiClient.get<TcmLicenseEligibility>(
      `/tcm/coverage/${tenantId}/eligibility`
    )
    return response.data
  },

  // Capacity scan (large-client wave 3, H-8/H-9): licence tier, the customer's
  // own monitors and snapshot jobs, the room left for Guardian's monitor, and
  // the next sweep's gate verdict. Tenant-scoped read (PA OR tenant access);
  // cached server-side for five minutes, `refresh` re-reads Graph.
  async getCapacityScan(tenantId: string, refresh = false): Promise<TcmCapacityScan> {
    const response = await apiClient.get<TcmCapacityScan>(
      `/tcm/coverage/${tenantId}/capacity`,
      refresh ? { params: { refresh: true } } : undefined
    )
    return response.data
  },

  // Returns the rendered PowerShell connection script as text/plain — the
  // Monitoring tab downloads it client-side (Content-Disposition is
  // forwarded by the proxy but axios never applies it).
  // `includeExchange` defaults to false: the standard connect script defers
  // Exchange RBAC so the critical path needs only the Graph sign-in (decision
  // D2). The Exchange follow-up rung passes true to download the full script.
  async generateConnectScript(tenantId: string, includeExchange = false): Promise<string> {
    const response = await apiClient.post(
      `/tcm/coverage/${tenantId}/connect-script`,
      null,
      {
        responseType: 'text',
        transformResponse: [(d) => d],
        params: includeExchange ? { includeExchange: 'true' } : undefined,
      }
    )
    return response.data as string
  },

  // Verifies the pasted script output ({ payload, token }) end to end.
  // Returns 200 for both success and failure — `isValid` discriminates.
  async verifyConnection(
    tenantId: string,
    payload: TcmEnablementVerifyPayload,
    token: string
  ): Promise<TcmEnablementVerifyResult> {
    const response = await apiClient.post<TcmEnablementVerifyResult>(
      `/tcm/coverage/${tenantId}/verify`,
      { payload, token }
    )
    return response.data
  },

  // Kicks the sizing capture (server computes the type list). 202 with the
  // jobId to poll; 409 when the monthly allowance is exhausted.
  async startSizing(tenantId: string): Promise<TcmSizingStartResult> {
    const response = await apiClient.post<TcmSizingStartResult>(
      `/tcm/coverage/${tenantId}/size`,
      null
    )
    return response.data
  },

  // Polls the sizing capture. Customer-shaped status ('sizing'|'sized'|
  // 'failed'); `sizedGroups` carries per-group counts once archived.
  async getSizingStatus(
    tenantId: string,
    jobId: string
  ): Promise<TcmSizingStatus> {
    const response = await apiClient.get<TcmSizingStatus>(
      `/tcm/coverage/${tenantId}/size/${jobId}`
    )
    return response.data
  },

  // Registers the chosen coverage (profile names from SCOPE_PROFILES —
  // the server resolves profiles → types). 409s map via mapWriteError.
  async chooseScope(
    tenantId: string,
    profiles: string[]
  ): Promise<TcmScopeResult> {
    const response = await apiClient.post<TcmScopeResult>(
      `/tcm/coverage/${tenantId}/scope`,
      { profiles }
    )
    return response.data
  },

  // Switches continuous monitoring on (enable-only; deactivation stays
  // operator-side). 204 on success.
  async activateMonitoring(tenantId: string): Promise<void> {
    await apiClient.post(`/tcm/coverage/${tenantId}/activate`, null)
  },
}
